/*
  Bonzart: Diwali "sky cracker" celebration when a cart offer unlocks.
  - Watches the offer cards rendered by snippets/bonzart-offers.liquid
    ([data-bz-offers] with data-ship and data-pct).
  - When free shipping or a bigger % off unlocks, plays a short firework burst
    (about 2 s) with a quiet whoosh + bang + crackle sound (muted 10 pm - 7 am),
    and a small message.
  - After the crackers, as the sky clears, the unlocked offer line "pops" (grows a little,
    settles back, gold glow on the bold words) and a gold shine sweeps across its card.
  - No library, no audio files. Sound only plays after the shopper has tapped
    the page (browser rule). Reduced-motion users get the message only.
*/
(() => {
  if (window.__bzCelebrate) return;
  window.__bzCelebrate = true;

  const KEY = 'bz-offer-state';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- state ---------- */
  const readState = () => {
    const els = document.querySelectorAll('[data-bz-offers]');
    if (!els.length) return null;
    let ship = 0;
    let pct = 0;
    els.forEach((el) => {
      ship = Math.max(ship, Number(el.dataset.ship) || 0);
      pct = Math.max(pct, Number(el.dataset.pct) || 0);
    });
    return { ship, pct };
  };
  const loadSaved = () => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) || 'null');
    } catch (e) {
      return null;
    }
  };
  const save = (s) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(s));
    } catch (e) {
      /* storage blocked: celebrations still work for this page view */
    }
  };

  let last = loadSaved();
  const first = readState();
  if (first) {
    if (!last) last = first;
    else if (first.pct > last.pct || (first.ship && !last.ship)) {
      // unlocked while on another page (e.g. redirected to the cart page)
      const before = last;
      setTimeout(() => celebrate(first, before), 600);
    }
    last = first;
    save(last);
  }

  let timer;
  const check = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const now = readState();
      if (!now) {
        // offer cards disappear when the cart is emptied: start over
        last = { ship: 0, pct: 0 };
        save(last);
        return;
      }
      const prev = last || now;
      if (now.pct > prev.pct || (now.ship && !prev.ship)) celebrate(now, prev);
      last = now;
      save(now);
    }, 180);
  };

  const touchesOffers = (n) =>
    n.nodeType === 1 && (n.hasAttribute('data-bz-offers') || !!n.closest('[data-bz-offers]') || !!n.querySelector('[data-bz-offers]'));
  new MutationObserver((list) => {
    for (const m of list) {
      if (m.type === 'attributes' || [...m.addedNodes].some(touchesOffers)) {
        check();
        return;
      }
    }
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-ship', 'data-pct'] });
  document.addEventListener('cart:update', check);

  /* ---------- sound (Web Audio, generated) ---------- */
  let audio;
  const unlockAudio = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audio) audio = new AC();
      if (audio.state === 'suspended') audio.resume();
    } catch (e) {
      audio = null;
    }
  };
  ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => document.addEventListener(ev, unlockAudio, { passive: true, capture: true }));

  let noiseBuf;
  const noise = () => {
    if (noiseBuf) return noiseBuf;
    const len = audio.sampleRate;
    noiseBuf = audio.createBuffer(1, len, audio.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return noiseBuf;
  };
  const playNoise = (t, dur, filterType, f0, f1, g0, out) => {
    const src = audio.createBufferSource();
    src.buffer = noise();
    const f = audio.createBiquadFilter();
    f.type = filterType;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = audio.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(g0, t + Math.min(0.02, dur / 4));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(out);
    src.start(t, Math.random() * 0.5, dur + 0.05);
  };
  const crackerSound = (launches) => {
    if (!audio || audio.state !== 'running') return;
    // Kept low on purpose, and silent from 10 pm to 7 am (shopper's own clock).
    const hr = new Date().getHours();
    if (hr >= 22 || hr < 7) return;
    const out = audio.createGain();
    out.gain.value = 0.12;
    out.connect(audio.destination);
    const now = audio.currentTime + 0.02;
    launches.forEach(({ delay, flight }) => {
      const t = now + delay;
      playNoise(t, flight, 'bandpass', 500, 2600, 0.18, out); // whoosh up
      const b = t + flight;
      playNoise(b, 0.5, 'lowpass', 1600, 220, 0.7, out); // soft bang
      const o = audio.createOscillator(); // thump
      const og = audio.createGain();
      o.frequency.setValueAtTime(110, b);
      o.frequency.exponentialRampToValueAtTime(40, b + 0.25);
      og.gain.setValueAtTime(0.2, b);
      og.gain.exponentialRampToValueAtTime(0.0001, b + 0.3);
      o.connect(og).connect(out);
      o.start(b);
      o.stop(b + 0.32);
      for (let i = 0; i < 16; i++) {
        playNoise(b + 0.35 + Math.random() * 0.8, 0.03, 'highpass', 3000, 5000, 0.12 + Math.random() * 0.12, out); // crackle
      }
    });
  };

  /* ---------- visuals ---------- */
  const injectStyle = () => {
    if (document.getElementById('bz-celebrate-style')) return;
    const st = document.createElement('style');
    st.id = 'bz-celebrate-style';
    st.textContent =
      '.bz-fx{position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483000}' +
      '.bz-fx-toast{position:fixed;z-index:2147483001;left:50%;transform:translate(-50%,-10px) scale(.94);opacity:0;' +
      'background:#2b2622;color:#fff;border-radius:999px;padding:9px 16px;font:600 13px/1.2 var(--font-body--family,system-ui,sans-serif);' +
      'white-space:nowrap;box-shadow:0 10px 24px -10px rgb(43 38 34/.6);transition:transform .35s cubic-bezier(.2,1.4,.4,1),opacity .3s ease;pointer-events:none}' +
      '.bz-fx-toast.is-on{transform:translate(-50%,0) scale(1);opacity:1}' +
      /* unlocked offer line: pop + gold glow, and a shine sweep across its card */
      '.bz-unlock-text{transform-origin:left center;animation:bzTextPop .65s cubic-bezier(.2,1.5,.4,1)}' +
      '.bz-unlock-text strong{animation:bzGlow 1.3s ease-out}' +
      '.bz-unlock-card::before{content:"";position:absolute;top:0;bottom:0;left:-60%;width:45%;z-index:2;pointer-events:none;' +
      'background:linear-gradient(100deg,transparent 0%,rgb(255 236 170/.85) 50%,transparent 100%);transform:skewX(-20deg);animation:bzShine .9s ease-out .1s both}' +
      '@keyframes bzTextPop{0%{transform:scale(1)}35%{transform:scale(1.05)}65%{transform:scale(.98)}100%{transform:scale(1)}}' +
      '@keyframes bzGlow{0%,100%{text-shadow:none}30%{text-shadow:0 0 10px rgb(255 193 77/.95),0 0 2px rgb(255 209 102/.9)}}' +
      '@keyframes bzShine{to{left:130%}}' +
      '@media (prefers-reduced-motion:reduce){.bz-unlock-text,.bz-unlock-text strong{animation:none}.bz-unlock-card::before{display:none}}';
    document.head.appendChild(st);
  };

  const visibleOffers = (host) => {
    const all = [...host.querySelectorAll('[data-bz-offers]')];
    return (
      all.find((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
      }) || null
    );
  };

  // Pop the unlocked line + shine its card. Looks the cards up again at play time,
  // because the cart may have re-rendered them since the unlock was detected.
  const popUnlocked = (host, isShip) => {
    const o = visibleOffers(host) || visibleOffers(document);
    if (!o) return;
    const card = o.querySelector(isShip ? '.bz-card--ship, .bz-slim' : '.bz-card:not(.bz-card--ship)');
    const line = card && card.querySelector(isShip ? '.bz-card__text, .bz-slim__line--ship' : '.bz-card__text, .bz-slim__line');
    if (!card || !line) return;
    card.classList.remove('bz-unlock-card');
    line.classList.remove('bz-unlock-text');
    void card.offsetWidth; // restart the animation if it is already playing
    card.classList.add('bz-unlock-card');
    line.classList.add('bz-unlock-text');
    setTimeout(() => {
      card.classList.remove('bz-unlock-card');
      line.classList.remove('bz-unlock-text');
    }, 1700);
  };

  function celebrate(now, prev) {
    injectStyle();
    const drawer = document.querySelector('dialog.cart-drawer__dialog[open]');
    const host = drawer || document.body;
    const offers = visibleOffers(host) || visibleOffers(document);
    const isShip = !(now.pct > prev.pct);
    const msg = isShip ? '🪔 Yay! FREE shipping unlocked' : `🪔 Shubh Deepavali! ${now.pct}% off unlocked`;

    // message
    const toast = document.createElement('div');
    toast.className = 'bz-fx-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = msg;
    const cr = offers ? offers.getBoundingClientRect() : null;
    const top = cr ? Math.min(cr.bottom + 10, window.innerHeight - 60) : 90;
    toast.style.top = `${top}px`;
    if (cr) toast.style.left = `${cr.left + cr.width / 2}px`;
    host.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-on'));
    setTimeout(() => toast.classList.remove('is-on'), 2600);
    setTimeout(() => toast.remove(), 3100);

    if (reduceMotion) return;
    // crackers first; the offer text pops as the sky clears (fallback after 2.6 s)
    let popped = false;
    const pop = () => {
      if (popped) return;
      popped = true;
      popUnlocked(host, isShip);
    };
    setTimeout(pop, 2600);
    fireworks(host, cr, !!drawer, pop);
  }

  function fireworks(host, cardRect, inDrawer, onClear) {
    const canvas = document.createElement('canvas');
    canvas.className = 'bz-fx';
    canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(canvas);
    const box = canvas.getBoundingClientRect();
    const W = box.width;
    const H = box.height;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const GOLD = ['#ffd166', '#ffe9a8', '#f7b52c', '#ffffff', '#ffc14d'];
    const FEST = ['#ffd166', '#f08a1c', '#f7b52c', '#ff6b3d', '#ffffff', '#e8a33d'];
    const rnd = (a, b) => a + Math.random() * (b - a);
    const pick = (a) => a[(Math.random() * a.length) | 0];
    const maxNight = inDrawer ? 0.72 : 0.5;

    // card position inside the canvas
    const c = cardRect
      ? { x: cardRect.left - box.left, y: cardRect.top - box.top, w: cardRect.width, h: cardRect.height }
      : { x: W * 0.2, y: H * 0.35, w: W * 0.6, h: 60 };
    const startY = Math.min(H - 20, c.y + c.h + 140);
    const scale = Math.max(1, Math.min(1.6, W / 380));
    const FLIGHT = 26; // frames (~0.43 s)
    const launches = [
      { x: c.x + c.w * 0.3, y: Math.max(40, c.y - 20), d: 0, pal: GOLD },
      { x: c.x + c.w * 0.72, y: Math.max(60, c.y + 10), d: 14, pal: FEST },
      { x: c.x + c.w * 0.5, y: Math.max(30, c.y - 40), d: 30, pal: GOLD }
    ];
    crackerSound(launches.map((l) => ({ delay: l.d / 60, flight: FLIGHT / 60 })));

    let parts = [];
    let frame = 0;
    let night = 0;
    const spark = (x, y, vx, vy, o) => parts.push({ x, y, px: x, py: y, vx, vy, g: o.g, drag: o.drag, c: o.c, w: o.w, life: o.life, max: o.life, crackle: o.crackle, kind: o.kind || 'spark', onEnd: o.onEnd });
    const burst = (x, y, pal) => {
      const n = 90;
      const base = pick(pal);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + rnd(-0.05, 0.05);
        const v = rnd(2.6, 5.2) * scale;
        spark(x, y, Math.cos(a) * v, Math.sin(a) * v, { g: 0.05, drag: 0.965, c: Math.random() < 0.6 ? base : pick(pal), w: 1.8, life: rnd(45, 70), crackle: 1 });
      }
      spark(x, y, 0, 0, { g: 0, drag: 1, c: '#fff', w: 12, life: 4, crackle: 0 });
    };

    const tick = () => {
      frame++;
      launches.forEach((l, k) => {
        if (frame !== l.d + 1) return;
        const sx = c.x + c.w * (0.35 + 0.15 * k);
        spark(sx, startY, (l.x - sx) / FLIGHT, (l.y - startY) / FLIGHT, { g: 0, drag: 1, c: '#ffd678', w: 2.4, life: FLIGHT, crackle: 0, kind: 'rocket', onEnd: (p) => burst(p.x, p.y, l.pal) });
      });

      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      const busy = frame < 40 || parts.length > 25;
      night += ((busy ? maxNight : 0) - night) * (busy ? 0.18 : 0.08);
      ctx.fillStyle = `rgba(28,18,12,${night.toFixed(3)})`;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      parts = parts.filter((p) => p.life > 0);
      parts.forEach((p) => {
        p.px = p.x;
        p.py = p.y;
        p.vx *= p.drag;
        p.vy = p.vy * p.drag + p.g;
        p.x += p.vx;
        p.y += p.vy;
        p.life--;
        const a = Math.max(0, Math.min(1, p.life / (p.max * 0.5)));
        if (p.kind === 'rocket') {
          ctx.strokeStyle = `rgba(255,214,120,${a})`;
          ctx.lineWidth = 2.4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 4, p.y - p.vy * 4);
          ctx.stroke();
          if (p.life <= 0 && p.onEnd) p.onEnd(p);
          return;
        }
        if (p.crackle && p.life < p.max * 0.45 && Math.random() < 0.5) {
          ctx.fillStyle = `rgba(255,255,255,${a})`;
          ctx.fillRect(p.x - 1, p.y - 1, 2, 2);
          return;
        }
        ctx.globalAlpha = a;
        ctx.strokeStyle = p.c;
        ctx.lineWidth = p.w;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(p.px - p.vx * 1.5, p.py - p.vy * 1.5);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      if (onClear && frame > 40 && night < 0.4) {
        onClear();
        onClear = null;
      }
      if (frame < 40 || parts.length || night > 0.01) requestAnimationFrame(tick);
      else canvas.remove();
    };
    requestAnimationFrame(tick);
  }
})();
